import React, { useEffect } from 'react';

import {
  RadioGroupOption,
  Grid,
  LegacySwitchView,
  Checkbox,
  Box,
  Stack,
  Section,
} from '@gogame/components';
import { FormField, FormFieldArray, getIn } from '@gogame/form';
import { useMountedState } from '@gogame/hooks-ts';

import { useUploadFile } from 'scenes/IO/data/mutations';

import {
  AGREEMENT_TYPE,
  ATTACHMENTS,
  ATTACHMENT_TYPE,
  SIGNED_BY_PUBLISHER,
} from '../constants';
import AgreementUriField from './AgreementUriField';
import {
  requiredAttachAgreement,
  requiredFrameAgreement,
  requiredAttachIO,
  requiredAgreementWithIo,
  validateAgreementUri,
} from './validation';

export const INTERNAL = 0;
export const PRE_SET = 1;
export const PARTNER = 2;

const Agreement = (props) => {
  const { value = {}, name, form, disabled } = props;
  const [separateFiles, setSeparateFiles] = useMountedState(false);
  const initialValue = getIn(form.initialValues, name);

  const cleanAttachments = () =>
    form.setFieldValue(`${name}.${ATTACHMENTS}`, [], form.validateOnChange);

  const uploadFile = useUploadFile();

  const handleUploadFile = async (file, type) => {
    const response = await uploadFile(file);
    return { ...response, type };
  };

  const handleChangeRequiredFrame = (_e, v) => {
    setSeparateFiles(v);
    cleanAttachments();
  };

  const handleChangeSeparateFiles = (_e, v) => {
    setSeparateFiles(v);
    cleanAttachments();
  };

  const handleValidateFrameAgreement = (attach, meta) => {
    if (separateFiles) {
      return requiredFrameAgreement(attach, meta);
    }
  };

  const handleValidateAttachAgreement = (attach, meta) => {
    if (separateFiles && !value) {
      return requiredAttachAgreement(attach, meta);
    }
  };

  const handleValidateAttachIO = (attach, meta) => {
    if (separateFiles && !attach) {
      return requiredAttachIO(attach, meta);
    }
  };

  const disabledSignByPublisher = value[AGREEMENT_TYPE] === PRE_SET;

  const handleChangeType = (_, value) => {
    if (value === PRE_SET && !form.values[SIGNED_BY_PUBLISHER]) {
      form.setFieldValue(SIGNED_BY_PUBLISHER, true);
    } else if (disabledSignByPublisher) {
      form.setFieldValue(SIGNED_BY_PUBLISHER, undefined);
    }
    form.setFieldValue(`${name}.${ATTACHMENTS}`, []);
    setSeparateFiles(false);
  };

  useEffect(() => {
    if (initialValue) {
      setSeparateFiles((initialValue[ATTACHMENTS] || []).length === 2);
    }
  }, [initialValue]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Section title={'Agreement Type'}>
          <FormField.RadioGroup
            name={`${name}.${AGREEMENT_TYPE}`}
            radioVertical={true}
            vertical={true}
            onChange={handleChangeType}
            padding={0}
          >
            {disabled}

            <RadioGroupOption
              label={"Internal's"}
              value={INTERNAL}
              disabled={disabled}
            />

            <RadioGroupOption
              label={"Partner's"}
              value={PARTNER}
              disabled={disabled}
            />

            <RadioGroupOption
              label={"Pre-set partner's"}
              value={PRE_SET}
              disabled={disabled}
            />
          </FormField.RadioGroup>
        </Section>
      </Grid>
      <Grid item xs={6}>
        <Section title={'Attachments'}>
          <Grid container>
            <Grid item>
              <FormField.Checkbox
                name={SIGNED_BY_PUBLISHER}
                label={'Signed by partner'}
                disabled={disabledSignByPublisher}
                padding={0}
              />
            </Grid>
            <Grid item xs={12}>
              <FormFieldArray name={`${name}.${ATTACHMENTS}`}>
                {({ remove }) => (
                  <LegacySwitchView activeId={`${value[AGREEMENT_TYPE]}`}>
                    <LegacySwitchView.Fragment viewId={`${INTERNAL}`}>
                      <Stack spacing={3}>
                        <Checkbox
                          label={'Requires a frame agreement'}
                          onChange={handleChangeRequiredFrame}
                          checked={separateFiles}
                          disabled={disabled}
                          padding={0}
                        />
                        <FormField.AttachmentField
                          label={'Attach Agreement + IO'}
                          accept={'.pdf, .docx'}
                          tooltip={'Only docx or pdf files are allowed'}
                          name={`${name}.${ATTACHMENTS}.[0]`}
                          onUpload={(file) =>
                            handleUploadFile(
                              file,
                              ATTACHMENT_TYPE.AgreementWithIo
                            )
                          }
                          validate={requiredAttachAgreement}
                          variant={'outlined'}
                          direction={'column'}
                          padding={0}
                        />
                        {separateFiles ? (
                          <FormField.AttachmentField
                            label={'Attach Frame Agreement'}
                            accept={'.pdf, .docx'}
                            tooltip={'Only docx or pdf files are allowed'}
                            name={`${name}.${ATTACHMENTS}.[1]`}
                            validate={handleValidateFrameAgreement}
                            onUpload={(file) =>
                              handleUploadFile(
                                file,
                                ATTACHMENT_TYPE.FrameAgreement
                              )
                            }
                            variant={'outlined'}
                            direction={'column'}
                            padding={0}
                          />
                        ) : null}
                      </Stack>
                    </LegacySwitchView.Fragment>
                    <LegacySwitchView.Fragment viewId={`${PARTNER}`}>
                      <Stack spacing={3}>
                        <Checkbox
                          label={'Agreement and IO are separate files'}
                          onChange={handleChangeSeparateFiles}
                          checked={separateFiles}
                          disabled={disabled}
                        />
                        {!separateFiles ? (
                          <FormField.AttachmentField
                            label={'Attach Agreement + IO'}
                            accept={'.pdf, .docx'}
                            tooltip={'Only docx or pdf files are allowed'}
                            name={`${name}.${ATTACHMENTS}.[0]`}
                            validate={requiredAgreementWithIo}
                            onUpload={(file) =>
                              handleUploadFile(
                                file,
                                ATTACHMENT_TYPE.AgreementWithIo
                              )
                            }
                            variant={'outlined'}
                            direction={'column'}
                            padding={0}
                          />
                        ) : null}
                        {separateFiles ? (
                          <Stack spacing={2}>
                            <FormField.AttachmentField
                              accept={'.pdf, .docx'}
                              tooltip={'Only docx or pdf files are allowed'}
                              label={'Attach Agreement'}
                              name={`${name}.${ATTACHMENTS}.[0]`}
                              validate={handleValidateAttachAgreement}
                              onUpload={(file) =>
                                handleUploadFile(
                                  file,
                                  ATTACHMENT_TYPE.Agreement
                                )
                              }
                              loading={disabled}
                              variant={'outlined'}
                              direction={'column'}
                              padding={0}
                            />
                            <FormField.AttachmentField
                              accept={'.pdf, .docx'}
                              tooltip={'Only docx or pdf files are allowed'}
                              label={'Attach IO'}
                              name={`${name}.${ATTACHMENTS}.[1]`}
                              validate={handleValidateAttachIO}
                              onUpload={(file) =>
                                handleUploadFile(file, ATTACHMENT_TYPE.Io)
                              }
                              variant={'outlined'}
                              direction={'column'}
                              padding={0}
                            />
                          </Stack>
                        ) : null}
                      </Stack>
                    </LegacySwitchView.Fragment>
                    <LegacySwitchView.Fragment viewId={`${PRE_SET}`}>
                      <Box pt={3}>
                        <AgreementUriField
                          remove={remove}
                          validate={validateAgreementUri}
                          name={`${name}.${ATTACHMENTS}.[0]`}
                          padding={0}
                        />
                      </Box>
                    </LegacySwitchView.Fragment>
                  </LegacySwitchView>
                )}
              </FormFieldArray>
            </Grid>
          </Grid>
        </Section>
      </Grid>
    </Grid>
  );
};

export default Agreement;
